import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Dispatch, bindActionCreators } from 'redux';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { RootState } from '../../reducers';
import * as actions from '../../actions/generalActions';
import * as productionActions from '../../actions/production';
import { withRouter } from 'react-router';
import { toast } from 'react-toastify';
import { isMobile } from '../../constants/constants';
import { convertNumberToHMS } from '../Admin/globalFunctions';
import { axiosCaller } from '../../utils/apiCaller';
import { SelectOption } from '../../types/selectOption.type';
import { useUrlFilters } from '../../hooks/useUrlFilters';

import MikaPageHeader from '../../components/MikaPageHeader/MikaPageHeader';
import MikaTable, { MikaColumn } from '../../components/MikaTable/MikaTable';
import MikaDateFilter, { DateRange } from '../../components/MikaDateFilter/MikaDateFilter';
import MikaExport, { ExportColumn } from '../../components/MikaExport/MikaExport';
import MikaViewToggle from '../../components/MikaViewToggle/MikaViewToggle';
import ContactDashboardStrip from '../Contact/ContactDashboardStrip';
import PaginationComponent from '../../components/Pagination/PaginationComponent';
import '../../components/Pagination/Pagination.scss';
import '../Contact/Contact.scss';
import ArchivedResponsibilityCardGrid from './ArchivedResponsibilityCardGrid';
import SearchablePopover from './SearchablePopover';
import ReturnHistoryDetail from './ReturnHistoryDetail';

interface Props {
    history: any;
    location: any;
    actions: any;
    productionActions: any;
    intl: any;
    permissions: any;
    buttonLoader: boolean;
    departments: SelectOption[];
    loading: any;
    archivedMaterialsAndResponsibilitiesPaginated: any;
}

const VIEW_STORAGE_KEY = 'archivedRespViewMode';

const ArchivedResponsibilitiesPage: React.FC<Props> = (props) => {
    const intl = useIntl();
    const fm = useCallback(
        (id: string, defaultMessage: string, values?: Record<string, any>) =>
            intl.formatMessage({ id, defaultMessage }, values),
        [intl]
    );

    const { filters, setFilter, setFilters, replaceFilters } = useUrlFilters(props.history, props.location, {
        page: { default: 1, type: 'number' },
        perPage: { default: 20, type: 'number' },
        search: { default: '', type: 'string' },
        deptId: { default: 0, type: 'number' },
        dateFrom: { default: '', type: 'string' },
        dateTo: { default: '', type: 'string' },
        workerId: { default: 0, type: 'number' },
        workerName: { default: '', type: 'string' },
        processId: { default: 0, type: 'number' },
        processName: { default: '', type: 'string' },
        materialId: { default: 0, type: 'number' },
        materialName: { default: '', type: 'string' },
        hasReturns: { default: 0, type: 'number' },
    });

    // Derived variables
    const currentPage = filters.page;
    const perPage = filters.perPage;
    const searchTerm = filters.search;
    const selectedDepartmentId = filters.deptId || null;
    const dateRange: DateRange | null = filters.dateFrom && filters.dateTo ? { from: filters.dateFrom, to: filters.dateTo } : null;
    const activeWorkerId = filters.workerId || null;
    const activeWorkerName = filters.workerName;
    const activeProcessId = filters.processId || null;
    const activeProcessName = filters.processName;
    const activeMaterialId = filters.materialId || null;
    const activeMaterialName = filters.materialName;
    const hasReturnsFilter = filters.hasReturns === 1;

    // Setter wrappers
    const setCurrentPage = (p: number) => setFilter('page', p);
    const setSelectedDepartmentId = (v: any) => setFilters({ deptId: v || 0, page: 1 });
    const setDateRange = (r: DateRange | null) => {
        setFilters({ dateFrom: r?.from || '', dateTo: r?.to || '', page: 1 });
    };

    const [viewMode, setViewMode] = useState<'table' | 'card'>(() => {
        return (localStorage.getItem(VIEW_STORAGE_KEY) as 'table' | 'card') || 'table';
    });

    // Dashboard
    const [statsData, setStatsData] = useState<any>(null);
    const [workerPopoverOpen, setWorkerPopoverOpen] = useState(false);
    const [processPopoverOpen, setProcessPopoverOpen] = useState(false);
    const [materialPopoverOpen, setMaterialPopoverOpen] = useState(false);
    const [workerPopoverPos, setWorkerPopoverPos] = useState({ top: 0, left: 0 });
    const [processPopoverPos, setProcessPopoverPos] = useState({ top: 0, left: 0 });
    const [materialPopoverPos, setMaterialPopoverPos] = useState({ top: 0, left: 0 });
    const workerRef = useRef<HTMLDivElement>(null);
    const processRef = useRef<HTMLDivElement>(null);
    const materialRef = useRef<HTMLDivElement>(null);
    const stripRef = useRef<HTMLDivElement>(null);

    // Return-to-process modal
    const [showReturnModal, setShowReturnModal] = useState(false);
    const [selectedRow, setSelectedRow] = useState<any>(null);
    const [adminComment, setAdminComment] = useState('');
    const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
    const [returnCategories, setReturnCategories] = useState<any[]>([]);

    // Return detail drill-down
    const [returnDetailRow, setReturnDetailRow] = useState<any>(null);

    // Data
    const [transformedData, setTransformedData] = useState<any[]>([]);

    const loadStats = () => {
        props.productionActions.getArchivedResponsibilitiesDashboardStats().then((res: any) => {
            if (res) setStatsData(res);
        }).catch(() => {});
    };

    useEffect(() => {
        props.actions.getAllDepartments();
        loadStats();
        props.productionActions.fetchReturnCategories().then((cats: any) => {
            if (cats) setReturnCategories(cats);
        });
    }, []);

    useEffect(() => {
        fetchData();
    }, [props.location.search]);

    const fetchData = () => {
        props.actions.getArchivedMaterialsAndResponsibilities(
            currentPage,
            selectedDepartmentId || null,
            dateRange?.from || null,
            dateRange?.to || null,
            null,
            activeProcessId || null,
            activeWorkerId || null,
            searchTerm || null,
            perPage,
            activeMaterialId || undefined,
            hasReturnsFilter || undefined
        );
    };

    const handleSearch = (term: string) => {
        replaceFilters({ search: term, page: 1 });
    };

    // Transform API data
    useEffect(() => {
        const dataArray = props.archivedMaterialsAndResponsibilitiesPaginated?.data || [];
        const transformed = dataArray.map((row: any) => ({
            ...row,
            clientName: row.client?.clientName,
            projectName: row.project?.projectName,
            articleName: row.product?.articleName,
            materialName: row.material?.materialName,
            userName: row.user?.userName,
            duration: row.productiveSeconds ? convertNumberToHMS(row.productiveSeconds) : (row.totalDuration ? convertNumberToHMS(row.totalDuration) : '00h:00m:00s'),
            durationSeconds: row.productiveSeconds || row.totalDuration || 0,
            elapsedSeconds: row.totalDuration || 0,
            productiveSeconds: row.productiveSeconds || 0,
            returnCount: row.returnCount || 0,
            adminReturnCount: row.adminReturnCount || 0,
            workerBlockCount: row.workerBlockCount || 0,
        }));
        setTransformedData(transformed);
    }, [props.archivedMaterialsAndResponsibilitiesPaginated]);

    // Dashboard strip
    const dashboardStats = useMemo(() => {
        const s = statsData;
        const avgSec = s?.avgDuration || 0;
        const avgLabel = avgSec > 0 ? convertNumberToHMS(avgSec) : '\u2014';
        return [
            { key: 'total',       label: fm('production.productArchive.strip.total', 'Total'),              value: s?.total ?? 0,                    icon: 'users' },
            { key: 'returned',    label: fm('production.responsibilityArchive.strip.returned', 'Të kthyera'), value: s?.returnedCount ?? 0,          icon: 'alert' },
            { key: 'avgDuration', label: fm('production.responsibilityArchive.strip.avgTime', 'Koha mesatare'), value: avgLabel,                    icon: 'check' },
            { key: 'workers',     label: fm('production.dashboard.workers', 'Punëtorët'),                   value: (s?.workers || []).length,       icon: 'agent' },
            { key: 'processes',   label: fm('production.board.processes', 'Proceset'),                     value: (s?.processes || []).length,     icon: 'folder' },
            { key: 'materials',   label: fm('production.expansion.materials', 'Materialet'),               value: (s?.materials || []).length,     icon: 'alert' },
        ];
    }, [statsData, fm]);

    const workers: { id: number; name: string; count: number; avgDuration: number }[] = useMemo(() => statsData?.workers || [], [statsData]);
    const processes: { id: number; name: string; count: number; avgDuration: number }[] = useMemo(() => statsData?.processes || [], [statsData]);
    const materials: { id: number; name: string; count: number; avgDuration: number }[] = useMemo(() => statsData?.materials || [], [statsData]);

    // Map of process avgDuration by responsibility_id for duration color coding
    const processAvgMap = useMemo(() => {
        const map: Record<number, number> = {};
        processes.forEach(p => { map[p.id] = p.avgDuration; });
        return map;
    }, [processes]);


    const getPopoverPos = (cardIndex: number) => {
        if (!stripRef.current) return { top: 0, left: 0 };
        const cards = stripRef.current.querySelectorAll('.cds-card');
        const card = cards[cardIndex];
        if (!card) return { top: 0, left: 0 };
        const stripRect = stripRef.current.getBoundingClientRect();
        const cardRect = card.getBoundingClientRect();
        return { top: cardRect.bottom - stripRect.top + 4, left: cardRect.left + cardRect.width / 2 - stripRect.left };
    };

    const closeAllPopovers = () => { setWorkerPopoverOpen(false); setProcessPopoverOpen(false); setMaterialPopoverOpen(false); };

    const handleStripClick = (key: string) => {
        if (key === 'returned') {
            closeAllPopovers();
            setFilters({ hasReturns: hasReturnsFilter ? 0 : 1, page: 1 });
        } else if (key === 'workers') {
            setProcessPopoverOpen(false); setMaterialPopoverOpen(false);
            if (!workerPopoverOpen) setWorkerPopoverPos(getPopoverPos(3));
            setWorkerPopoverOpen(!workerPopoverOpen);
        } else if (key === 'processes') {
            setWorkerPopoverOpen(false); setMaterialPopoverOpen(false);
            if (!processPopoverOpen) setProcessPopoverPos(getPopoverPos(4));
            setProcessPopoverOpen(!processPopoverOpen);
        } else if (key === 'materials') {
            setWorkerPopoverOpen(false); setProcessPopoverOpen(false);
            if (!materialPopoverOpen) setMaterialPopoverPos(getPopoverPos(5));
            setMaterialPopoverOpen(!materialPopoverOpen);
        } else {
            closeAllPopovers();
        }
    };

    const handleWorkerFilter = (w: { id: number; name: string }) => {
        setFilters({ workerId: w.id, workerName: w.name, processId: 0, processName: '', materialId: 0, materialName: '', page: 1 });
        setWorkerPopoverOpen(false);
    };

    const handleProcessFilter = (p: { id: number; name: string }) => {
        setFilters({ processId: p.id, processName: p.name, workerId: 0, workerName: '', materialId: 0, materialName: '', page: 1 });
        setProcessPopoverOpen(false);
    };

    const handleMaterialFilter = (m: { id: number; name: string }) => {
        setFilters({ materialId: m.id, materialName: m.name, workerId: 0, workerName: '', processId: 0, processName: '', page: 1 });
        setMaterialPopoverOpen(false);
    };

    const clearActiveFilter = () => {
        setFilters({ workerId: 0, workerName: '', processId: 0, processName: '', materialId: 0, materialName: '', page: 1 });
    };

    // Close popovers on outside click
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (workerRef.current && !workerRef.current.contains(e.target as Node)) setWorkerPopoverOpen(false);
            if (processRef.current && !processRef.current.contains(e.target as Node)) setProcessPopoverOpen(false);
            if (materialRef.current && !materialRef.current.contains(e.target as Node)) setMaterialPopoverOpen(false);
        };
        if (workerPopoverOpen || processPopoverOpen || materialPopoverOpen) document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [workerPopoverOpen, processPopoverOpen, materialPopoverOpen]);

    const handleViewChange = (mode: 'table' | 'card') => {
        setViewMode(mode);
        localStorage.setItem(VIEW_STORAGE_KEY, mode);
    };

    const effectiveView = isMobile ? 'table' : viewMode;

    // Return to process
    const openReturnModal = (row: any) => {
        if (!props.permissions?.fullPrivileges) return;
        setSelectedRow(row);
        setAdminComment('');
        setSelectedCategoryId(null);
        setShowReturnModal(true);
    };

    const returnToProcess = () => {
        if (!adminComment) { toast.error(fm('production.responsibilityArchive.toast.commentRequired', 'Komenti është obligativ!')); return; }
        const payload: any = {
            materialId: selectedRow?.material?.materialId,
            productId: selectedRow?.product?.productId,
            responsibilityStatusId: 3,
            responsibilityId: selectedRow?.responsibilityId,
            adminWarning: adminComment,
        };
        if (selectedCategoryId) payload.returnCategoryId = selectedCategoryId;
        const endpoint = selectedRow?.material?.materialId
            ? 'responsibilities/clients/material/statuses'
            : 'responsibilities/clients/statuses';

        axiosCaller('POST', endpoint, payload).then(() => {
            toast.success(fm('production.responsibilityArchive.toast.success', 'Procesi u kthye me sukses'));
            setShowReturnModal(false);
            fetchData();
            loadStats();
        }).catch(() => toast.error(fm('production.toast.somethingWentWrong', 'Diçka nuk shkoi mirë!')));
    };

    // Format elapsed time as human-readable (e.g., "2d 8h" or "3h 20m")
    const formatElapsed = (seconds: number): string => {
        if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
        if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
        const days = Math.floor(seconds / 86400);
        const hours = Math.floor((seconds % 86400) / 3600);
        return `${days}d ${hours}h`;
    };

    // Duration color: green if under avg, amber if 1-2x, red if over 2x
    const getDurationColor = (seconds: number, responsibilityId: number) => {
        const avg = processAvgMap[responsibilityId];
        if (!avg || !seconds) return { color: '#475569', bg: 'transparent' };
        const ratio = seconds / avg;
        if (ratio <= 1) return { color: '#16a34a', bg: '#dcfce7' };
        if (ratio <= 2) return { color: '#d97706', bg: '#fef3c7' };
        return { color: '#dc2626', bg: '#fef2f2' };
    };

    // Columns
    const columns: MikaColumn[] = useMemo(() => [
        { key: 'clientName',   title: fm('production.detail.client', 'Klienti'),   sortable: true, render: (val: any) => val || '\u2014' },
        { key: 'projectName',  title: fm('production.detail.project', 'Projekti'), hideOnMobile: true, render: (val: any) => val || '\u2014' },
        { key: 'articleName',  title: fm('production.board.productCol', 'Produkti'), hideOnMobile: true, render: (val: any) => val || '\u2014' },
        { key: 'materialName', title: fm('production.responsibilityArchive.col.material', 'Materiali'), hideOnMobile: true, render: (val: any) => val || '\u2014' },
        {
            key: 'responsibilityName', title: fm('production.responsibilityArchive.col.process', 'Procesi'),
            render: (val: any, row: any) => (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    {val || '\u2014'}
                    {row.returnCount > 0 && (
                        <span
                            title={fm('production.responsibilityArchive.returnTooltip', 'Kthyer {admin}x, Bllokuar {blocked}x — kliko për detaje', { admin: row.adminReturnCount, blocked: row.workerBlockCount })}
                            onClick={(e) => { e.stopPropagation(); setReturnDetailRow(row); }}
                            style={{
                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                minWidth: 18, height: 18, borderRadius: 9, background: '#fef3c7', color: '#d97706',
                                fontSize: 9, fontWeight: 700, cursor: 'pointer', flexShrink: 0,
                                padding: '0 4px', transition: 'transform 0.1s'
                            }}
                            onMouseEnter={e => (e.currentTarget.style.transform = 'scale(1.2)')}
                            onMouseLeave={e => (e.currentTarget.style.transform = 'scale(1)')}
                        >{row.returnCount}</span>
                    )}
                </span>
            )
        },
        {
            key: 'duration', title: fm('production.responsibilityArchive.col.duration', 'Koha'), hideOnMobile: true,
            render: (_: any, row: any) => {
                const productive = row.productiveSeconds || 0;
                const elapsed = row.elapsedSeconds || 0;
                const dc = getDurationColor(productive || elapsed, row.responsibilityId);
                return (
                    <div style={{ lineHeight: 1.4 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <span style={{ fontSize: 9, color: '#64748b', width: 32 }}>{fm('production.responsibilityArchive.col.productive', 'Aktive')}</span>
                            <span style={{
                                fontFamily: 'monospace', fontSize: 11, padding: '1px 5px', borderRadius: 3,
                                color: dc.color, background: dc.bg
                            }}>{productive > 0 ? convertNumberToHMS(productive) : '\u2014'}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <span style={{ fontSize: 9, color: '#94a3b8', width: 32 }}>{fm('production.responsibilityArchive.col.total', 'Totale')}</span>
                            <span style={{ fontFamily: 'monospace', fontSize: 10, color: '#94a3b8' }}>
                                {elapsed > 0 ? convertNumberToHMS(elapsed) : '\u2014'}
                            </span>
                        </div>
                    </div>
                );
            }
        },
        { key: 'userName',       title: fm('production.responsibilityArchive.col.user', 'Përdoruesi'),     hideOnMobile: true, render: (val: any) => val || '\u2014' },
        { key: 'departmentName', title: fm('production.responsibilityArchive.col.department', 'Reparti'),  hideOnMobile: true, render: (val: any) => val || '\u2014' },
        { key: 'trackersStart',  title: fm('production.responsibilityArchive.col.start', 'Fillimi'),       hideOnMobile: true, render: (val: any) => val || '\u2014' },
        { key: 'trackersEnd',    title: fm('production.responsibilityArchive.col.end', 'Mbarimi'),         hideOnMobile: true, render: (val: any) => val || '\u2014' },
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [intl.locale, processAvgMap]);

    const tableActions = props.permissions?.fullPrivileges ? [
        {
            icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="1 4 1 10 7 10" /><path d="M3.51 15a9 9 0 105.64-11.36L1 10" /></svg>,
            tooltip: fm('production.responsibilityArchive.returnAction', 'Kthe në proces'),
            onClick: (row: any) => openReturnModal(row)
        }
    ] : [];

    const pagination = props.archivedMaterialsAndResponsibilitiesPaginated;

    // Export
    const exportColumns: ExportColumn[] = useMemo(() => [
        { key: 'clientName',         title: fm('production.detail.client', 'Klienti') },
        { key: 'projectName',        title: fm('production.detail.project', 'Projekti') },
        { key: 'articleName',        title: fm('production.board.productCol', 'Produkti') },
        { key: 'materialName',       title: fm('production.responsibilityArchive.col.material', 'Materiali') },
        { key: 'responsibilityName', title: fm('production.responsibilityArchive.col.process', 'Procesi') },
        { key: 'duration',           title: fm('production.responsibilityArchive.col.duration', 'Koha') },
        { key: 'userName',           title: fm('production.responsibilityArchive.col.user', 'Përdoruesi') },
        { key: 'departmentName',     title: fm('production.responsibilityArchive.col.department', 'Reparti') },
        { key: 'trackersStart',      title: fm('production.responsibilityArchive.col.start', 'Fillimi') },
        { key: 'trackersEnd',        title: fm('production.responsibilityArchive.col.end', 'Mbarimi') },
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [intl.locale]);

    const fetchAllForExport = async (): Promise<any[]> => {
        let queryParams: string[] = ['per_page=10000'];
        if (selectedDepartmentId) queryParams.push(`departmentId=${selectedDepartmentId}`);
        if (dateRange?.from) queryParams.push(`startDate=${dateRange.from}`);
        if (dateRange?.to) queryParams.push(`endDate=${dateRange.to}`);
        if (searchTerm) queryParams.push(`search=${encodeURIComponent(searchTerm)}`);
        if (activeWorkerId) queryParams.push(`userId=${activeWorkerId}`);
        if (activeProcessId) queryParams.push(`responsibilityId=${activeProcessId}`);
        if (activeMaterialId) queryParams.push(`materialId=${activeMaterialId}`);
        const res = await axiosCaller('GET', `archive/statuses?${queryParams.join('&')}`);
        const raw = res?.data?.data || [];
        return raw.map((r: any) => ({
            clientName: r.client?.clientName,
            projectName: r.project?.projectName,
            articleName: r.product?.articleName,
            materialName: r.material?.materialName,
            responsibilityName: r.responsibilityName,
            duration: r.totalDuration ? convertNumberToHMS(r.totalDuration) : '00h:00m:00s',
            userName: r.user?.userName,
            departmentName: r.departmentName,
            trackersStart: r.trackersStart,
            trackersEnd: r.trackersEnd,
        }));
    };


    return (
        <div className="clp-page">
            <MikaPageHeader title={fm('production.responsibilityArchive.title', 'Arkiva e proceseve')} />

            {/* Department tabs */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 0', overflowX: 'auto' }}>
                <button className={`clp-dept-tab ${selectedDepartmentId === null ? 'clp-dept-tab-active' : ''}`}
                    onClick={() => setFilters({ deptId: 0, page: 1 })} type="button">
                    <FormattedMessage id="common.pagination.all" defaultMessage="Të gjitha" />
                </button>
                {(props.departments || []).map((dept: any) => (
                    <button key={dept.value} className={`clp-dept-tab ${selectedDepartmentId === dept.value ? 'clp-dept-tab-active' : ''}`}
                        onClick={() => setFilters({ deptId: dept.value, page: 1 })} type="button">
                        {dept.label}
                    </button>
                ))}
            </div>

            <div className="clp-compact-toolbar">
                <div className="clp-search-wrap">
                    <svg className="clp-search-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
                    </svg>
                    <input type="text" className="clp-search-input" placeholder={fm('production.responsibilityArchive.searchPlaceholder', 'Kërko procese...')}
                        value={searchTerm} onChange={(e) => handleSearch(e.target.value)} />
                    {searchTerm && (
                        <button className="clp-search-clear" onClick={() => handleSearch('')} type="button">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" /></svg>
                        </button>
                    )}
                </div>
                <div className="clp-toolbar-actions">
                    <MikaDateFilter value={dateRange} onChange={(r) => setDateRange(r)} />
                    {!isMobile && <MikaViewToggle mode={viewMode} onChange={handleViewChange} />}
                    <MikaExport fetchAllData={fetchAllForExport} columns={exportColumns} fileName={fm('production.responsibilityArchive.exportFilename', 'arkiva_proceseve')} title={fm('production.responsibilityArchive.title', 'Arkiva e proceseve')} companyProfile={(props as any).companyProfile} />
                </div>
            </div>

            <div ref={stripRef} style={{ position: 'relative' }}>
                    <ContactDashboardStrip stats={dashboardStats} onFilterClick={handleStripClick} />

                    {/* Workers popover */}
                    {workerPopoverOpen && workers.length > 0 && (
                        <SearchablePopover
                            popoverRef={workerRef}
                            items={workers}
                            label={fm('production.responsibilityArchive.popover.worker', 'Punëtori')}
                            searchPlaceholder={fm('production.responsibilityArchive.popover.workerSearch', 'Kërko punëtor...')}
                            activeId={activeWorkerId}
                            onSelect={handleWorkerFilter}
                            showRank
                            style={{ top: workerPopoverPos.top, left: workerPopoverPos.left, transform: 'translateX(-50%)' }}
                        />
                    )}

                    {/* Processes popover */}
                    {processPopoverOpen && processes.length > 0 && (
                        <SearchablePopover
                            popoverRef={processRef}
                            items={processes}
                            label={fm('production.responsibilityArchive.popover.process', 'Procesi')}
                            searchPlaceholder={fm('production.responsibilityArchive.popover.processSearch', 'Kërko proces...')}
                            activeId={activeProcessId}
                            onSelect={handleProcessFilter}
                            style={{ top: processPopoverPos.top, left: processPopoverPos.left, transform: 'translateX(-50%)' }}
                        />
                    )}

                    {/* Materials popover */}
                    {materialPopoverOpen && materials.length > 0 && (
                        <SearchablePopover
                            popoverRef={materialRef}
                            items={materials}
                            label={fm('production.responsibilityArchive.popover.material', 'Materiali')}
                            searchPlaceholder={fm('production.responsibilityArchive.popover.materialSearch', 'Kërko material...')}
                            activeId={activeMaterialId}
                            onSelect={handleMaterialFilter}
                            style={{ top: materialPopoverPos.top, left: materialPopoverPos.left, transform: 'translateX(-50%)' }}
                        />
                    )}
            </div>

            {/* Active filter chip */}
            {(activeWorkerId || activeProcessId || activeMaterialId || hasReturnsFilter) && (
                <div className="clp-active-filter-bar">
                    {hasReturnsFilter && (
                        <div className="clp-active-filter-chip">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="1 4 1 10 7 10" /><path d="M3.51 15a9 9 0 105.64-11.36L1 10" />
                            </svg>
                            <span><FormattedMessage id="production.responsibilityArchive.strip.returned" defaultMessage="Të kthyera" /></span>
                            <span className="clp-active-filter-count"><FormattedMessage id="production.responsibilityArchive.countLabel" defaultMessage="{count} procese" values={{ count: pagination?.total ?? 0 }} /></span>
                            <button className="clp-active-filter-clear" onClick={() => setFilters({ hasReturns: 0, page: 1 })} type="button">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                    <path d="M18 6L6 18M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    )}
                    {(activeWorkerId || activeProcessId || activeMaterialId) && (
                        <div className="clp-active-filter-chip">
                            {activeWorkerId ? (
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" />
                                </svg>
                            ) : activeMaterialId ? (
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" />
                                </svg>
                            ) : (
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                                </svg>
                            )}
                            <span>{activeWorkerId ? activeWorkerName : activeMaterialId ? activeMaterialName : activeProcessName}</span>
                            <span className="clp-active-filter-count"><FormattedMessage id="production.responsibilityArchive.countLabel" defaultMessage="{count} procese" values={{ count: pagination?.total ?? 0 }} /></span>
                            <button className="clp-active-filter-clear" onClick={clearActiveFilter} type="button">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                    <path d="M18 6L6 18M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    )}
                </div>
            )}

            {effectiveView === 'table'
                ? <MikaTable
                    columns={columns}
                    data={transformedData}
                    actions={tableActions}
                    loading={props.loading}
                    onRowClick={props.permissions?.fullPrivileges ? openReturnModal : undefined}
                />
                : <ArchivedResponsibilityCardGrid data={transformedData} onAction={openReturnModal} />
            }

            <PaginationComponent
                currentPage={currentPage} perPage={perPage} pagination={pagination}
                onPageChange={(p) => setFilter('page', p)} onPerPageChange={(v) => setFilters({ perPage: v, page: 1 })}
                label={fm('production.responsibilityArchive.recordsLabel', 'procese')}
            />

            {/* Return to process modal */}
            {showReturnModal && selectedRow && (
                <div className="agr-modal-overlay" onClick={() => setShowReturnModal(false)}>
                    <div className="agr-modal" style={{ width: 420, maxWidth: '92vw' }} onClick={e => e.stopPropagation()}>
                        <div className="agr-modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span><FormattedMessage id="production.responsibilityArchive.modal.title" defaultMessage="Rikthimi në proces" /></span>
                            <button onClick={() => setShowReturnModal(false)} type="button" style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 18 }}>&times;</button>
                        </div>
                        <div className="agr-modal-body">
                            <div style={{ marginBottom: 12, fontSize: 13, color: '#475569', lineHeight: 1.6 }}>
                                <div><strong><FormattedMessage id="production.detail.client" defaultMessage="Klienti" />:</strong> {selectedRow.clientName}</div>
                                <div><strong><FormattedMessage id="production.responsibilityArchive.col.process" defaultMessage="Procesi" />:</strong> {selectedRow.responsibilityName}</div>
                                <div><strong><FormattedMessage id="production.board.productCol" defaultMessage="Produkti" />:</strong> {selectedRow.articleName}</div>
                                {selectedRow.materialName && <div><strong><FormattedMessage id="production.responsibilityArchive.col.material" defaultMessage="Materiali" />:</strong> {selectedRow.materialName}</div>}
                                <div><strong><FormattedMessage id="production.responsibilityArchive.col.user" defaultMessage="Përdoruesi" />:</strong> {selectedRow.userName}</div>
                                {selectedRow.duration && <div><strong><FormattedMessage id="production.productArchive.modal.duration" defaultMessage="Kohëzgjatja" />:</strong> <span style={{ fontFamily: 'monospace' }}>{selectedRow.duration}</span></div>}
                            </div>

                            {selectedRow.returnCount > 0 && (
                                <div style={{ marginBottom: 12, padding: '8px 12px', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 8, fontSize: 12 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#92400e', fontWeight: 600, marginBottom: 4 }}>
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>
                                        {selectedRow.adminReturnCount > 0 && fm('production.responsibilityArchive.modal.returnedTimes', 'Kthyer {count} herë', { count: selectedRow.adminReturnCount })}
                                        {selectedRow.adminReturnCount > 0 && selectedRow.workerBlockCount > 0 && ' · '}
                                        {selectedRow.workerBlockCount > 0 && fm('production.responsibilityArchive.modal.blockedTimes', 'Bllokuar {count} herë', { count: selectedRow.workerBlockCount })}
                                    </div>
                                    {selectedRow.adminWarning && (
                                        <div style={{ color: '#78350f', lineHeight: 1.4 }}>
                                            <strong><FormattedMessage id="production.responsibilityArchive.modal.lastReason" defaultMessage="Arsyeja e fundit:" /></strong> {selectedRow.adminWarning}
                                        </div>
                                    )}
                                </div>
                            )}

                            {returnCategories.length > 0 && (
                                <div style={{ marginBottom: 12 }}>
                                    <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', marginBottom: 4, display: 'block' }}><FormattedMessage id="production.responsibilityArchive.modal.categoryLabel" defaultMessage="Kategoria e kthimit" /></label>
                                    <select
                                        value={selectedCategoryId || ''}
                                        onChange={e => setSelectedCategoryId(e.target.value ? Number(e.target.value) : null)}
                                        style={{ width: '100%', padding: '8px 10px', border: '1px solid #d0dbe8', borderRadius: 6, fontSize: 12, outline: 'none', fontFamily: 'inherit', background: '#fff', color: '#334155' }}
                                    >
                                        <option value="">{fm('production.responsibilityArchive.modal.selectCategory', 'Zgjedh kategorinë (opsionale)')}</option>
                                        {returnCategories.map((cat: any) => (
                                            <option key={cat.id} value={cat.id}>{cat.name}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            <div>
                                <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', marginBottom: 4, display: 'block' }}><FormattedMessage id="production.responsibilityArchive.modal.commentLabel" defaultMessage="Komenti për administrator *" /></label>
                                <textarea
                                    value={adminComment}
                                    onChange={e => setAdminComment(e.target.value)}
                                    placeholder={fm('production.responsibilityArchive.modal.commentPlaceholder', 'Shkruaj arsyen e rikthimit...')}
                                    rows={3}
                                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #d0dbe8', borderRadius: 6, fontSize: 12, resize: 'none', outline: 'none', fontFamily: 'inherit' }}
                                />
                            </div>
                        </div>
                        <div className="agr-modal-actions">
                            <button className="agr-btn-secondary" onClick={() => setShowReturnModal(false)} type="button"><FormattedMessage id="common.action.cancel" defaultMessage="Anulo" /></button>
                            <button className="agr-btn-primary" onClick={returnToProcess} type="button" disabled={props.buttonLoader}><FormattedMessage id="production.responsibilityArchive.returnAction" defaultMessage="Kthe në proces" /></button>
                        </div>
                    </div>
                </div>
            )}

            {/* Return history detail drill-down */}
            {returnDetailRow && (
                <ReturnHistoryDetail
                    productId={returnDetailRow.product?.productId}
                    responsibilityId={returnDetailRow.responsibilityId}
                    materialId={returnDetailRow.material?.materialId || null}
                    title={`${returnDetailRow.articleName || ''} — ${returnDetailRow.responsibilityName || ''} ${returnDetailRow.materialName ? '(' + returnDetailRow.materialName + ')' : ''}`}
                    onClose={() => setReturnDetailRow(null)}
                    permissions={props.permissions}
                />
            )}
        </div>
    );
};

function mapStateToProps(state: RootState, ownProps: any) {
    return {
        ...ownProps,
        ...state.app,
    };
}

function mapDispatchToProps(dispatch: Dispatch<actions.ACTION>) {
    return {
        productionActions: bindActionCreators(productionActions as any, dispatch),
        actions: bindActionCreators(actions as any, dispatch),
    };
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(injectIntl(ArchivedResponsibilitiesPage as any)));
